import { useState, type FormEvent } from "react";
import { MapPinned, Pencil, Plus, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { useCreateSite, useDeleteSite, useSites, useUpdateSite } from "./hooks";
import type { Site } from "./api";

export default function SitesPage() {
  const { data: sites, isLoading, isError, error } = useSites();
  const create = useCreateSite();
  const update = useUpdateSite();
  const remove = useDeleteSite();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  function openCreate() {
    setEditing(null);
    setName("");
    setDescription("");
    setActionError(null);
    setFormOpen(true);
  }

  function openEdit(s: Site) {
    setEditing(s);
    setName(s.name);
    setDescription(s.description ?? "");
    setActionError(null);
    setFormOpen(true);
  }

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body: { name, description } });
      } else {
        await create.mutateAsync({ name, description });
      }
      setFormOpen(false);
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sites</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The top level of your structure: Site → Customers → Offices. Create a
            site first, then add customers into it and assign site staff.
          </p>
        </div>
        <RequirePermission permission="site:manage">
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add site
          </Button>
        </RequirePermission>
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Site</th>
              <th className="px-4 py-2.5 font-medium">Code</th>
              <th className="px-4 py-2.5 font-medium">Customers</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load sites."}
                </td>
              </tr>
            )}
            {sites?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No sites yet. Click “Add site” to create your first one.
                </td>
              </tr>
            )}
            {sites?.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium">
                    <MapPinned className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    {s.name}
                  </div>
                  {s.description && (
                    <div className="text-xs text-muted-foreground">{s.description}</div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                <td className="px-4 py-3 text-muted-foreground">{s.customer_count}</td>
                <td className="px-4 py-3">
                  <Badge tone={s.is_active ? "success" : "danger"}>
                    {s.is_active ? "Active" : "Inactive"}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <RequirePermission permission="site:manage">
                      <Button variant="ghost" aria-label={`Edit ${s.name}`} onClick={() => openEdit(s)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          void run(() =>
                            update.mutateAsync({ id: s.id, body: { is_active: !s.is_active } }),
                          )
                        }
                      >
                        {s.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <ConfirmButton
                        aria-label={`Remove ${s.name}`}
                        title="Delete site?"
                        message={
                          <>Delete site <span className="font-medium text-foreground">{s.name}</span>? This can’t be undone.</>
                        }
                        onConfirm={() => run(() => remove.mutateAsync(s.id))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ConfirmButton>
                    </RequirePermission>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Edit site" : "Add site"}>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {actionError && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {actionError}
            </div>
          )}
          <Field label="Site name" htmlFor="site_name">
            <Input id="site_name" required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Description (optional)" htmlFor="site_desc">
            <Input id="site_desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || create.isPending || update.isPending}>
              {editing ? "Save changes" : "Create site"}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
